import { Component, ElementRef, HostBinding, HostListener, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { OrganigramChatDockService, OrgChatMessage, OrgConversation } from '../../services/organigram-chat-dock.service';
import { OrganigramApiService } from '../../services/organigram-api.service';
import { DirNode, DirRow, DirTree, buildDirectoryTree, visibleRows, isChattableNodeType } from '../../services/organigram-chat-directory';
import { LIVE_FALLBACK, thinkingLabel } from '../../services/organigram-chat-trace';
import { OrgTask, localDateKey, openTaskCount, taskDateLabel } from '../../services/organigram-tasks';
import { OrganigramPermissionsService } from '../../services/organigram-permissions.service';
import { can } from '../../mona-app/workflow-graph/logic/organigram-permission';

// One node's conversations collapsed into a single Recent entry: the newest is shown, all of them
// sit behind the count chip. Keyed by org+node so two orgs sharing a node id never merge.
interface RecentGroup {
  key: string;
  orgId: string;
  nodeName: string;
  nodeType?: string;
  latest: OrgConversation;
  items: OrgConversation[];
}

// Cross-company agent search results grouped by company, so each company is a labelled sub-header
// with its matching agents/teams listed (and indented) beneath it.
interface AgentGroup { orgId: string; orgName: string; hits: DirNode[]; }

// Fixed width of the collapsed "slim tall dock" (the panel with its history sidebar dropped): a narrow,
// full-height chat window, OG-Facebook style. The big/comprehensive panel is a % of the viewport.
const SLIM_PANEL_PX = 480;

// OG-Facebook-style chat dock with a two-mode rail, like every messenger: it opens on your recent
// conversations, and a "New chat" button switches to the org directory (an indented tree) to start a
// fresh chat. Typing runs one unified search across BOTH - your chats and the directory - shown in
// sections. The big panel's left sidebar mirrors the same structure. App-level; backend is the source
// of truth (nothing persisted to localStorage - see OrganigramChatDockService).
@Component({
  selector: 'app-organigram-chat-dock',
  templateUrl: './organigram-chat-dock.component.html',
  styleUrls: ['./organigram-chat-dock.component.scss']
})
export class OrganigramChatDockComponent implements OnDestroy {
  // App-level, so there is no parent to pass the selection down - read it from the API service.
  private get organigramId(): string { return this.api.currentOrganigramId; }
  search = '';
  railMode: 'chats' | 'new' = 'chats';   // 'chats' = recent conversations, 'new' = browse the directory (via the New-chat button)
  searchFocused = false;                 // focusing the empty search opens the directory browse too, then reverts on blur
  recentExpanded: Record<string, boolean> = {};   // group key -> its extra sessions are shown in Recent
  // The directory spans every company now: one tree per org, cached. `browseOrgId` picks which company
  // the New-chat tree shows (blank = follow the app's current company); search spans all loaded trees.
  browseOrgId = '';
  private trees = new Map<string, DirTree>();
  private loadedOrgs = new Set<string>();
  private loadingOrgs = new Set<string>();
  private failedOrgs = new Set<string>();    // orgs whose directory failed to load (e.g. backend 500 on corrupt data)
  openNodes: Record<string, boolean> = {};   // nodeId -> team opened in the indented tree
  loadingDir = false;                        // spinner for the company currently shown in Browse
  companyPickerOpen = false;                 // the custom company dropdown in Browse is expanded
  panelWidth: number | null = null;          // px override for the panel; null = CSS default (~46vw)
  comprehensive = false;                     // stage 3: comprehensive mode with the left sidebar
  tasksOpen: boolean | null = null;          // Assigned-to-you strip; null = follow the default (see tasksShown)
  private subs = new Subscription();

  // Approving or rejecting a paused execution is organigram.operate. The dock is mounted app-wide, so
  // this getter is the only thing here that knows about permissions - nothing else in the dock changes.
  get canOperate(): boolean { return can(this.permissions.current, 'organigram.operate'); }

  // Without organigram.view the whole dock is gone - bar, rail and panel - the same rule that replaces the
  // /organigram page. On the host, so the template stays as it is; the service reads nothing either.
  @HostBinding('style.display') get hostDisplay(): string | null { return this.dock.blocked ? 'none' : null; }

  constructor(public dock: OrganigramChatDockService, private api: OrganigramApiService,
              private host: ElementRef<HTMLElement>,
              private permissions: OrganigramPermissionsService) {
    const w = Number(localStorage.getItem('ocd-panel-width'));
    if (w >= 360) { this.panelWidth = w; }
    this.comprehensive = localStorage.getItem('ocd-panel-expanded') === '1';
    // Trees are cached per company and survive the switch, so nothing is dropped here. When Browse is
    // following the app's current company (no explicit pick), load + open that company's tree on switch.
    this.subs.add(this.api.currentOrganigramId$.subscribe(() => {
      if (!this.browseOrgId) { this.loadDirectory(this.organigramId).then(() => this.openAllTeams()); }
    }));
    // A refusal that lands after the directories loaded drops them too, like the service drops its chats.
    this.subs.add(this.permissions.load$.subscribe(() => {
      if (!this.dock.blocked) { return; }
      this.trees.clear(); this.loadedOrgs.clear(); this.failedOrgs.clear(); this.activeId = null;
    }));
  }

  // Both streams are root services, and this component is re-created whenever a page without the menu is
  // left - so they are dropped here, or every dead instance keeps loading directories on each switch.
  ngOnDestroy(): void { this.subs.unsubscribe(); }

  // Company shown in Browse (defaults to the app's current company until the user picks another).
  get browseOrg(): string { return this.browseOrgId || this.organigramId; }
  // The directory tree of the company Browse is showing; empty until that company's graph loads.
  get tree(): DirTree { return this.trees.get(this.browseOrg) || { roots: [], all: [], byId: new Map() }; }
  // Companies for the "start a new chat" picker (only worth showing when there is more than one).
  get browseCompanies(): { id: string; name: string }[] { return this.dock.companyList; }
  pickCompany(orgId: string): void { this.browseOrgId = orgId; this.failedOrgs.delete(orgId); this.loadDirectory(orgId).then(() => this.openAllTeams()); }
  // Custom company dropdown (native <select> renders unreliably inside the panel). Choosing closes it.
  chooseCompany(orgId: string): void { this.companyPickerOpen = false; this.pickCompany(orgId); }
  // A readable company label: the real name, or "Untitled company" when it has none (stored name === id).
  companyLabel(orgId: string): string { const n = this.dock.companyName(orgId); return n && n !== orgId ? n : 'Untitled company'; }

  // Which conversation the big panel shows. Held on the service, so a view outside this component
  // (the graph's composer island) can open the chat it just started.
  get activeId(): string | null { return this.dock.focusedId; }
  set activeId(id: string | null) { this.dock.focusedId = id; }

  toggleRail(): void {
    this.dock.toggleRail();
    if (this.dock.railOpen) { this.ensureDirectories(); }
  }

  // --- Thinking block (the org hops behind one answer) ---
  // Shown from the moment the request goes out - it IS the "working" indicator - and afterwards only
  // when there is a trace worth opening.
  showThink(c: OrgConversation, m: OrgChatMessage): boolean {
    if (m.trace?.length) { return true; }
    return !!c.sending && m.role === 'assistant' && !m.content;
  }
  // Only worth a chevron once there is something inside to read.
  canOpenThink(m: OrgChatMessage): boolean { return !!m.trace?.length; }
  toggleThink(m: OrgChatMessage): void { if (this.canOpenThink(m)) { m.traceOpen = !m.traceOpen; } }
  // Live: whoever the org is asking right now (paced by the service). Settled: how long it took.
  thinkLabel(m: OrgChatMessage): string {
    return m.traceDone ? thinkingLabel(true, m.traceMs) : (m.traceLabel || LIVE_FALLBACK);
  }

  // Load one company's directory tree, cached and de-duped across calls. A company that already loaded
  // OR already failed is skipped, so a corrupt company (backend 500) is not re-fetched on every action.
  private async loadDirectory(orgId: string): Promise<void> {
    if (!orgId || this.loadedOrgs.has(orgId) || this.failedOrgs.has(orgId) || this.loadingOrgs.has(orgId)) { return; }
    this.loadingOrgs.add(orgId);
    if (orgId === this.browseOrg) { this.loadingDir = true; }
    try {
      if (!await this.dock.mayRead()) { return; }   // no organigram.view: nothing to browse
      const graph = await this.api.getGraph(orgId);   // parentName is set from the parent edges
      if (this.dock.blocked) { return; }
      this.trees.set(orgId, buildDirectoryTree(graph));
      this.loadedOrgs.add(orgId);
      if (orgId === this.browseOrg) { this.openAllTeams(); }
    } catch {
      this.failedOrgs.add(orgId);   // corrupt/unavailable company - the browse view says so; no retry spam
    }
    finally { this.loadingOrgs.delete(orgId); if (orgId === this.browseOrg) { this.loadingDir = false; } }
  }
  // True while the company Browse is showing failed to load (so the tree can explain the empty state).
  get browseFailed(): boolean { return this.failedOrgs.has(this.browseOrg); }

  // Load the Browse company first (fast display), then every other company in the background so the
  // agent search can span all of them. De-duped, so calling this liberally is cheap.
  private ensureDirectories(): void {
    this.loadDirectory(this.browseOrg);
    for (const c of this.dock.companyList) { this.loadDirectory(c.id); }
  }

  get searching(): boolean { return !!this.search.trim(); }

  // What the rail/sidebar list shows. Typing always wins (unified results); with an empty box, the
  // New-chat button (railMode) or simply focusing the search opens the directory browse; otherwise the
  // recent conversations.
  get railView(): 'results' | 'browse' | 'recent' {
    if (this.searching) { return 'results'; }
    if (this.railMode === 'new' || this.searchFocused) { return 'browse'; }
    return 'recent';
  }

  // Focusing the empty search opens the directory (start a new chat); blur returns to Recent. Browse
  // rows keep the input focused on mousedown (keepFocus), so blur only fires on a genuine exit.
  onSearchFocus(): void { this.searchFocused = true; this.ensureDirectories(); }
  // Keep a focus-opened browse alive when focus moves to a control INSIDE the dock - e.g. the company
  // picker, whose native dropdown steals focus and can't use keepFocus. Only focus leaving the dock
  // entirely (or nothing focused) returns to Recent. Some browsers report a null relatedTarget for a
  // <select>, so re-check where focus actually landed once it settles.
  onSearchBlur(ev: FocusEvent): void {
    const inDock = (n: Node | null) => !!n && this.host.nativeElement.contains(n);
    if (inDock(ev.relatedTarget as Node | null)) { return; }
    setTimeout(() => { if (!inDock(document.activeElement)) { this.searchFocused = false; } }, 0);
  }
  keepFocus(ev: Event): void { ev.preventDefault(); }   // clicking a browse row must not blur the search

  // Chats mode, no search: recent conversations, grouped by node so every session with the same agent
  // collapses into one entry (newest shown, all of them behind the count chip). Newest group first.
  get recentGroups(): RecentGroup[] {
    const byNode = new Map<string, OrgConversation[]>();
    for (const c of this.dock.conversations) {
      // Group by node within a company; node-less approvals (no node_id) key on their own id so two
      // distinct ones never merge into a single row.
      const key = `${c.organigramId}|${c.nodeId || c.id}`;
      const list = byNode.get(key);
      if (list) { list.push(c); } else { byNode.set(key, [c]); }
    }
    const groups: RecentGroup[] = [];
    byNode.forEach((items, key) => {
      items.sort((a, b) => b.updatedAt - a.updatedAt);
      groups.push({ key, orgId: items[0].organigramId, nodeName: items[0].nodeName, nodeType: items[0].nodeType, latest: items[0], items });
    });
    return groups.sort((a, b) => b.latest.updatedAt - a.latest.updatedAt).slice(0, 30);
  }

  // Chats span every company now, so a row/head is tagged with its company - but only when the user
  // actually has chats in more than one, so the single-company case stays clean.
  get multiCompany(): boolean {
    const orgs = new Set<string>();
    for (const c of this.dock.conversations) { orgs.add(c.organigramId); if (orgs.size > 1) { return true; } }
    return false;
  }
  // The account has more than one company. Unlike multiCompany (which counts companies you've CHATTED
  // in), this drives the cross-company SEARCH grouping - search spans every company you have, whether or
  // not you've chatted there yet.
  get manyCompanies(): boolean { return this.dock.companyList.length > 1; }
  companyName(orgId: string): string { return this.dock.companyName(orgId); }

  toggleRecent(key: string, ev: Event): void {
    ev.stopPropagation();
    this.recentExpanded[key] = !this.recentExpanded[key];
    if (!this.recentExpanded[key]) { return; }
    const g = this.recentGroups.find(x => x.key === key);
    if (g) { this.dock.ensureHistories(g.items); }
  }
  // Tooltip for the count chip. Every group carries the chip, single-chat ones included - without it
  // there is no way to tell an agent you have talked to once from one you have never opened.
  countLabel(n: number): string { return n === 1 ? '1 chat' : `${n} chats`; }
  // A group row is highlighted when any of its sessions is the one the panel currently shows.
  groupActive(items: OrgConversation[]): boolean { return !!this.activeId && items.some(c => c.id === this.activeId); }

  // Unified search, half 1: your conversations whose agent name or title matches.
  get chatResults(): OrgConversation[] {
    const q = this.search.trim().toLowerCase();
    if (!q) { return []; }
    return [...this.dock.conversations]
      .filter(c => (c.nodeName || '').toLowerCase().includes(q) || (c.title || '').toLowerCase().includes(q))
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 20);
  }

  // Unified search, half 2: directory nodes (agents/teams) to start a NEW chat with - across EVERY
  // company whose tree has loaded, grouped by company so each is a labelled sub-header. parentName comes
  // from the tree's parent edges, so a hit can name the team it sits in.
  get agentResults(): AgentGroup[] {
    const q = this.search.trim().toLowerCase();
    if (!q) { return []; }
    const groups: AgentGroup[] = [];
    const byOrg = new Map<string, AgentGroup>();
    let total = 0;
    for (const [orgId, tree] of this.trees) {
      for (const n of tree.all) {
        // The section is "Agents & teams" and means it: a human or a trigger is not a chat target,
        // so it never becomes a hit rather than becoming a row whose click is refused.
        if (!isChattableNodeType(n.type)) { continue; }
        if ((n.name || '').toLowerCase().includes(q)
          || (n.parentName || '').toLowerCase().includes(q)
          || (n.type || '').includes(q)) {
          let g = byOrg.get(orgId);
          if (!g) { g = { orgId, orgName: this.companyLabel(orgId), hits: [] }; byOrg.set(orgId, g); groups.push(g); }
          g.hits.push(n);
          if (++total >= 60) { return groups; }
        }
      }
    }
    return groups;
  }

  // New-chat mode: the org as one indented, scrollable tree. Teams open in place with their members
  // underneath, siblings stay visible, and there is no level to get stranded in.
  get railRows(): DirRow[] {
    return visibleRows(this.tree, id => !!this.openNodes[id]);
  }

  // Subtitle for a folded session: last user/assistant message, so two chats with the same node
  // are distinguishable. Falls back to a real title, then a loading / empty placeholder.
  convSub(c: OrgConversation): string {
    for (let i = (c.messages || []).length - 1; i >= 0; i--) {
      const m = c.messages[i];
      if (m.role === 'system') { continue; }
      const t = (m.content || '').replace(/\s+/g, ' ').trim();
      if (t) { return t; }
    }
    if (c.loadingHistory) { return 'Loading...'; }
    const t = (c.title || '').trim();
    const lower = t.toLowerCase();
    if (t && lower !== 'chat' && lower !== 'new chat' && !lower.startsWith('chat with ')) { return t; }
    return 'No messages yet';
  }

  // --- Mode switches --------------------------------------------------------------------------------
  showNew(): void { this.railMode = 'new'; this.search = ''; this.searchFocused = false; this.ensureDirectories(); }
  showChats(): void { this.railMode = 'chats'; this.search = ''; this.searchFocused = false; }
  // Leave the search/browse state so the next open (and the panel sidebar) starts on Recent.
  private resetSearch(): void { this.search = ''; this.searchFocused = false; this.railMode = 'chats'; this.companyPickerOpen = false; }

  // Row click in the tree: a team opens or closes, everyone else starts a chat.
  openRow(r: DirRow): void {
    if (r.expandable) { this.openNodes[r.node.id] = !r.expanded; } else { this.startChat(r.node); }
  }
  // Teams are chattable too, but their row is the open/close control - so this sits on its own button.
  chatWithNode(d: DirNode, ev: Event): void { ev.stopPropagation(); this.startChat(d); }

  // Teams start open: the point of the tree is seeing who is in them without hunting.
  private openAllTeams(): void {
    this.openNodes = {};
    for (const node of this.tree.all) {
      if (node.children.length) { this.openNodes[node.id] = true; }
    }
  }

  // The conversation the big panel shows: ONLY the explicitly-focused one. null → panel closed, and
  // every open convo minimizes to a bottom "chat head". Showing a convo marks it read.
  get active(): OrgConversation | null {
    if (!this.activeId) { return null; }
    const c = this.dock.windows.find(w => w.id === this.activeId) || null;
    if (c) { c.unread = false; }
    return c;
  }
  // Open conversations that aren't focused - rendered as bottom chat heads (old Facebook style).
  get heads(): OrgConversation[] { return this.dock.windows.filter(w => w.id !== this.activeId); }
  // Current panel width in px, so the chat heads dock just left of the panel's edge.
  get panelPx(): number {
    if (window.innerWidth <= 640) { return window.innerWidth; }   // media query forces the panel full-width on mobile
    if (!this.comprehensive) { return SLIM_PANEL_PX; }            // collapsed = a slim, tall dock (fixed width)
    if (this.panelWidth) { return this.panelWidth; }              // a resized big panel
    return Math.round(window.innerWidth * 0.64);
  }

  // Drag the panel's left edge to resize; the width persists to localStorage.
  startResize(ev: MouseEvent): void {
    ev.preventDefault();
    const move = (e: MouseEvent) => {
      this.panelWidth = Math.min(Math.max(window.innerWidth - e.clientX, 360), Math.round(window.innerWidth * 0.72));
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      if (this.panelWidth) { localStorage.setItem('ocd-panel-width', String(this.panelWidth)); }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }

  // --- Rail actions --- picking a chat opens it BIG (comprehensive, with the sidebar) and closes the
  // rail, so the two are never on screen together.
  // Start a chat in the node's OWN company (a search hit passes its org; a Browse-tree click defaults
  // to the company Browse is showing).
  // A human node is not a chat target, so the row does nothing rather than opening a dead window.
  // The directory hides those rows already; this keeps a stale search hit from getting through.
  startChat(d: DirNode, orgId?: string): void {
    // Chatting is organigram.operate. Gated HERE rather than on each button because the rail row,
    // the team row's own button and the search results all arrive through this one method.
    if (!this.canOperate) { return; }
    const org = orgId || this.browseOrg; this.comprehensive = true; this.ensureDirectories();
    const convo = this.dock.startNew(org, d.id, d.name, d.type);
    if (!convo) { return; }
    this.activeId = convo.id; this.dock.closeRail(); this.resetSearch();
  }
  openConvo(c: OrgConversation): void { this.comprehensive = true; this.ensureDirectories(); this.dock.openConversation(c); this.activeId = c.id; this.dock.closeRail(); this.resetSearch(); }

  // --- Panel actions ---
  closeWin(c: OrgConversation, ev: Event): void { ev.stopPropagation(); this.dock.closeWindow(c); if (this.activeId === c.id) { this.activeId = null; } }
  // Guarded for the conversations that already exist: a human chat started before this rule was
  // introduced is still in the rail, and its "new chat" button would otherwise reopen one.
  newChatSame(c: OrgConversation, ev: Event): void {
    ev.stopPropagation();
    if (!this.canOperate) { return; }
    const convo = this.dock.startNew(c.organigramId, c.nodeId, c.nodeName, c.nodeType);
    if (!convo) { return; }
    this.activeId = convo.id; this.dock.closeRail();
  }
  confirm(c: OrgConversation, approved: boolean): void { this.dock.confirmConversation(c, approved); }
  minimizePanel(): void { this.activeId = null; }                                      // demote the panel back to a bottom head (keep open)

  // Enter sends; Shift+Enter inserts a newline. Matches the agent-chat composer.
  onDraftKeydown(ev: KeyboardEvent, c: OrgConversation): void {
    if (ev.key !== 'Enter' || ev.shiftKey) { return; }
    ev.preventDefault();
    // Enter sends without touching the button, so it needs the same gate.
    if (!this.canOperate) { return; }
    this.dock.send(c);
    this.resetDraftHeight(ev.target as HTMLTextAreaElement);
  }
  sendDraft(c: OrgConversation, ev: Event): void {
    if (!this.canOperate) { return; }
    this.dock.send(c);
    const box = (ev.currentTarget as HTMLElement).closest('.ocd-input-box');
    this.resetDraftHeight(box?.querySelector('textarea') as HTMLTextAreaElement | null);
  }
  growDraft(ev: Event): void {
    const el = ev.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }
  private resetDraftHeight(el: HTMLTextAreaElement | null): void {
    if (el) { el.style.height = 'auto'; }
  }

  // Clicking the page outside the dock steps it down a size, never yanking the chat away: the rail
  // closes, then the big (comprehensive) panel drops its history sidebar to the slim panel, and only a
  // slim panel minimizes to a bottom head. Nothing is lost - one click brings the previous size back.
  // The whole host counts as inside, so the rail rows, the chat heads and the launcher keep working.
  // mousedown rather than click: a row that removes itself in its own click handler would otherwise
  // be reported as a target outside the dock, and a text selection dragged out of the panel would
  // close it on release. The dimmed mask is inside the host, so it has its own handler.
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(ev: MouseEvent): void {
    if (!this.activeId && !this.dock.railOpen) { return; }
    if (this.host.nativeElement.contains(ev.target as Node)) { return; }
    this.stepDownDock();
  }

  onBackdropMouseDown(ev: MouseEvent): void {
    ev.stopPropagation();
    this.stepDownDock();
  }

  private stepDownDock(): void {
    if (this.dock.railOpen) { this.dock.closeRail(); this.resetSearch(); }
    if (this.activeId) {
      if (this.comprehensive) { this.comprehensive = false; }
      else { this.minimizePanel(); }
    }
  }

  openHead(c: OrgConversation): void { this.activeId = c.id; c.unread = false; this.dock.closeRail(); this.ensureDirectories(); this.resetSearch(); }   // promote a head into the panel
  closeHead(c: OrgConversation, ev: Event): void { ev.stopPropagation(); this.dock.closeWindow(c); }

  // --- Stage 3: comprehensive mode (left sidebar mirrors the rail) ---
  toggleExpand(): void { this.comprehensive = !this.comprehensive; localStorage.setItem('ocd-panel-expanded', this.comprehensive ? '1' : ''); if (this.comprehensive) { this.ensureDirectories(); } }
  // Open a conversation picked from the sidebar into the main pane, without closing the panel.
  selectHistory(c: OrgConversation): void { this.dock.openConversation(c); this.activeId = c.id; c.unread = false; }

  relTime(ts: number): string {
    if (!ts) { return ''; }
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) { return 'now'; }
    const m = Math.floor(s / 60);
    if (m < 60) { return `${m}m`; }
    const h = Math.floor(m / 60);
    if (h < 24) { return `${h}h`; }
    return `${Math.floor(h / 24)}d`;
  }

  icon(type?: string): string {
    switch (type) {
      case 'team': return 'people-outline';
      case 'department': return 'business-outline';
      case 'division': return 'git-branch-outline';
      case 'agent': return 'hardware-chip-outline';
      case 'human': return 'person-outline';
      case 'pipeline': return 'funnel-outline';
      default: return 'chatbubble-ellipses-outline';
    }
  }

  // --- Assigned to you: the dailyTodos strip above Recent ---
  //
  // Read-only apart from the checkbox, and deliberately NOT presented as a conversation: a message
  // sent to a human node lands here as a todo with no sender and no thread, so a chat row promising
  // a reply would be a lie. See organigram-tasks.ts.
  get tasks(): OrgTask[] { return this.dock.tasks; }
  get openTasks(): number { return openTaskCount(this.dock.tasks); }
  // Collapsed by default once everything is done, so a finished list stops taking up the rail.
  get tasksShown(): boolean { return this.tasksOpen === null ? this.openTasks > 0 : this.tasksOpen; }
  toggleTasks(): void { this.tasksOpen = !this.tasksShown; }
  taskDate(t: OrgTask): string { return taskDateLabel(t.date, localDateKey(new Date())); }
  toggleTask(t: OrgTask, ev: Event): void { ev.stopPropagation(); this.dock.setTaskDone(t, !t.done); }
  trackByTask(_i: number, t: OrgTask): string { return t.id; }

  trackByConvo(_i: number, c: OrgConversation): string { return c.id; }
  trackByGroup(_i: number, g: RecentGroup): string { return g.key; }
  trackByNode(_i: number, n: DirNode): string { return n.id; }
  trackByOrgGroup(_i: number, g: AgentGroup): string { return g.orgId; }
  trackByCompany(_i: number, c: { id: string; name: string }): string { return c.id; }
  trackByRow(_i: number, r: DirRow): string { return r.node.id; }
  trackByIndex(i: number): number { return i; }
}
